import { useRive, useStateMachineInput } from '@rive-app/react-canvas';
import { Icon16Cancel } from '@vkontakte/icons';
import { type CustomSelectOptionProps, CustomSelect, CustomSelectOption, Input } from '@vkontakte/vkui';
import classNames from 'clsx';
import React, { type ChangeEvent, memo, useCallback, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { QuotaLandingSelectors } from 'reactApp/modules/quotaLanding/quotaLanding.selector';
import { NestleFormWrapperContext } from 'reactApp/sections/Nestle/FormWrapper/FormWrapper.context';
import { NESTLE_HIDE_ON_EXPORT_CLASS } from 'reactApp/sections/Nestle/FormWrapper/FormWrapper.helpers';
import {
    animations,
    animationsMobile,
    backgroundImages,
    backgroundImagesMobile,
} from 'reactApp/sections/Nestle/PetsCard/Animal/Animal.data';
import { animals } from 'reactApp/sections/Nestle/PetsCard/PetsCard.data';
import { Animals } from 'reactApp/sections/Nestle/PetsCard/PetsCard.types';

import commonStyles from '../PetsCard.css';
import styles from './Animal.css';

const RIVE_STATE_MACHINE_NAME = 'the best State Machine';
const RIVE_STATE_MACHINE_NAME_MOBILE = 'State Machine 1';
const RIVE_TRIGGER_NAME = 'Trigger 1';

export const Animal = memo(() => {
    const [animationSrc, setAnimationSrc] = useState<string>('');
    const [animationSrcMobile, setAnimationSrcMobile] = useState<string>('');

    const { formData, formErrors, setFormData, setFormErrors } = useContext(NestleFormWrapperContext);
    const { animal, cardColor } = formData;

    const isMobile = useSelector(QuotaLandingSelectors.isMobile);

    const backgroundImage = backgroundImages[cardColor];
    const backgroundImageMobile = backgroundImagesMobile[cardColor];

    const { rive, RiveComponent } = useRive({
        src: animationSrc,
        stateMachines: RIVE_STATE_MACHINE_NAME,
        autoplay: true,
    });

    const { rive: riveMobile, RiveComponent: RiveComponentMobile } = useRive({
        src: animationSrcMobile,
        stateMachines: RIVE_STATE_MACHINE_NAME_MOBILE,
        autoplay: true,
    });

    const triggerAnimation = useStateMachineInput(rive, RIVE_STATE_MACHINE_NAME, RIVE_TRIGGER_NAME);
    const triggerAnimationMobile = useStateMachineInput(riveMobile, RIVE_STATE_MACHINE_NAME_MOBILE, RIVE_TRIGGER_NAME);

    const renderSelectOptions = ({ option, ...restProps }: CustomSelectOptionProps) => {
        return (
            <CustomSelectOption {...restProps} selected={false} className={commonStyles.selectOption}>
                {option.label}
            </CustomSelectOption>
        );
    };

    const handleChange = useCallback(
        (event: ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
            setFormData((prev) => ({
                ...prev,
                animal: event.target.value,
                ...(event.target.value === Animals.other && { breed: '' }),
            }));

            if (formErrors.animal) {
                setFormErrors((prev) => ({ ...prev, animal: false }));
            }

            if (isMobile) {
                triggerAnimationMobile?.fire();
            } else {
                triggerAnimation?.fire();
            }
        },
        [setFormData, triggerAnimation, triggerAnimationMobile, isMobile, formErrors.animal, setFormErrors]
    );

    const handleChangeOther = useCallback(
        (event: ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
            setFormData((prev) => ({ ...prev, animalOther: event.target.value }));

            if (formErrors.animalOther) {
                setFormErrors((prev) => ({ ...prev, animalOther: false }));
            }

            if (isMobile) {
                triggerAnimationMobile?.fire();
            } else {
                triggerAnimation?.fire();
            }
        },
        [setFormData, triggerAnimation, triggerAnimationMobile, isMobile, formErrors.animalOther, setFormErrors]
    );

    const handleCancel = useCallback(() => {
        setFormData((prev) => ({
            ...prev,
            animal: '',
            animalOther: '',
            breed: '',
        }));
    }, [setFormData]);

    useEffect(() => {
        setAnimationSrc(animations[cardColor]);
        setAnimationSrcMobile(animationsMobile[cardColor]);
    }, [cardColor, setAnimationSrc, setAnimationSrcMobile]);

    return (
        <div className={classNames(commonStyles.field, commonStyles.animal)}>
            <div className={styles.animation}>
                <img src={backgroundImage} alt="animal-background" className={styles.image} />
                <img src={backgroundImageMobile} alt="animal-background" className={styles.imageMobile} />
                <div className={styles.animationDesktop}>
                    <RiveComponent key={animationSrc} />
                </div>
                <div className={styles.animationMobile}>
                    <RiveComponentMobile key={animationSrcMobile} />
                </div>
            </div>
            <span className={commonStyles.label}>просто</span>
            {animal === Animals.other ? (
                <Input
                    type="text"
                    placeholder="введите какое животное"
                    className={classNames(commonStyles.input, {
                        [commonStyles.inputError]: formErrors.animalOther,
                    })}
                    onChange={handleChangeOther}
                    after={
                        <div
                            className={classNames(styles.cancel, NESTLE_HIDE_ON_EXPORT_CLASS)}
                            onClick={handleCancel}
                            role="button"
                            aria-label="Сбросить"
                        >
                            <Icon16Cancel />
                        </div>
                    }
                />
            ) : (
                <CustomSelect
                    value={animal}
                    options={animals}
                    searchable={false}
                    placeholder="выберите какое животное"
                    className={classNames(commonStyles.select, {
                        [commonStyles.inputError]: formErrors.animal,
                    })}
                    renderOption={renderSelectOptions}
                    popupDirection="bottom"
                    forceDropdownPortal={false}
                    onChange={handleChange}
                />
            )}
            <span className={commonStyles.label}>мечты</span>
        </div>
    );
});

Animal.displayName = 'Animal';
